import {useState} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {defaultCreateAppData, ICreateAppData} from './IAppModels'
import {KTIcon} from '../../../helpers'
import {Step1} from './steps/Step1'
import {useAuth} from '../../../../app/modules/auth'
import {toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

type Props = {
  show: boolean
  handleClose: () => void
  claimDetails: any
  formattedDate: string
  onSuccessSubmit: () => void
}

const modalsRoot = document.getElementById('root-modals') || document.body

const CreateAppModal = ({
  show,
  handleClose,
  claimDetails,
  formattedDate,
  onSuccessSubmit,
}: Props) => {
  const user = useAuth()
  const {auth} = useAuth()
  const [data, setData] = useState<ICreateAppData>(defaultCreateAppData)
  const [loading, setLoading] = useState(false)
  const [dischargeDate, setDischargeDate] = useState<string>('')

  // Add state to track files for each category
  const [fileCategories, setFileCategories] = useState({
    resumeMedis: [],
    billingAkhir: [],
    hasilPenunjang: [],
  })

  // Check if all required categories have at least one file
  const hasAllRequiredFiles = Object.values(fileCategories).every((files) => files.length > 0)

  const updateData = (fieldsToUpdate: Partial<ICreateAppData>) => {
    const updatedData = {...data, ...fieldsToUpdate}
    setData(updatedData)
  }

  const handleFileUpload = (category: string, files: File[]) => {
    // Instead of appending, we replace the entire array for the category
    setFileCategories((prev) => ({
      ...prev,
      [category]: files,
    }))
  }

  const handleDischargeDate = (date: string) => {
    setDischargeDate(date)
  }

  const submit = async () => {
    if (!hasAllRequiredFiles) {
      toast.error('Please upload at least one file for each category')
      return
    }

    setLoading(true)
    try {
      // Rest of the status update code remains the same
      const statusChangeResponse = await fetch(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/UpdateStatus`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
          body: JSON.stringify({
            claimNumber: claimDetails.claimDetails.claimNumber,
            status: 'Request FGL',
            username: user.currentUser?.email,
            source: 'Provider',
            dischargeDate: dischargeDate,
          }),
        }
      )

      if (!statusChangeResponse.ok) {
        throw new Error('Failed to update status to Request FGL')
      }

      toast.success('FGL Request submitted successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })

      onSuccessSubmit()
      handleClose()
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error: ' + (error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return createPortal(
    <Modal
      data-testid='create-app-modal'
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-1000px'
      show={show}
      onHide={handleClose}
      backdrop={true}
    >
      <div
        className='modal-header'
        style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
      >
        <h2>Request FGL</h2>
        <div
          className='btn btn-sm btn-icon btn-active-color-primary'
          onClick={handleClose}
          style={{
            marginLeft: 'auto', // This pushes the button to the right
            cursor: 'pointer',
          }}
        >
          <KTIcon className='fs-1' iconName='cross' />
        </div>
      </div>

      <div className='modal-body py-lg-10 px-lg-10'>
        <div className='row'>
          {/* Left Section - Document Requirements */}
          <div className='col-md-4'>
            <div className='required-docs-section'>
              <h4 className='mb-4'>Required Documents</h4>
              <div className='doc-requirement mb-3'>
                <div className='d-flex align-items-center mb-2'>
                  <span className='bullet bullet-dot bg-primary me-2'></span>
                  <span className='fw-bold'>Resume Medis</span>
                </div>
                <div className='text-muted small'>
                  Files uploaded: {fileCategories.resumeMedis.length}
                </div>
              </div>
              <div className='doc-requirement mb-3'>
                <div className='d-flex align-items-center mb-2'>
                  <span className='bullet bullet-dot bg-primary me-2'></span>
                  <span className='fw-bold'>Billing Akhir</span>
                </div>
                <div className='text-muted small'>
                  Files uploaded: {fileCategories.billingAkhir.length}
                </div>
              </div>
              <div className='doc-requirement mb-3'>
                <div className='d-flex align-items-center mb-2'>
                  <span className='bullet bullet-dot bg-primary me-2'></span>
                  <span className='fw-bold'>Hasil Penunjang Medis</span>
                </div>
                <div className='text-muted small'>
                  Files uploaded: {fileCategories.hasilPenunjang.length}
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Upload Form */}
          <div className='col-md-8'>
            <form noValidate id='kt_modal_create_app_form'>
              <Step1
                data={data}
                updateData={updateData}
                hasError={false}
                onFileUpload={handleFileUpload}
                fileCategories={fileCategories}
                onDischargeDateChange={handleDischargeDate}
                claimNumber={claimDetails.claimDetails.claimNumber}
              />
            </form>
          </div>
        </div>

        {/* Submit button moved outside the form and positioned at the bottom right */}
        <div className='d-flex justify-content-end mt-5'>
          <button
            type='button'
            className='btn btn-lg btn-primary'
            onClick={submit}
            disabled={loading || !hasAllRequiredFiles}
            style={{
              backgroundColor: hasAllRequiredFiles ? '#059BB6' : '#E0E0E0',
              cursor: hasAllRequiredFiles ? 'pointer' : 'not-allowed',
              minWidth: '120px', // Ensure button has enough width
            }}
          >
            {loading ? (
              <span>
                <span
                  className='spinner-border spinner-border-sm'
                  role='status'
                  aria-hidden='true'
                ></span>{' '}
                Submitting...
              </span>
            ) : (
              <>
                Submit <KTIcon iconName='arrow-right' className='fs-3 ms-2 me-0' />
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}

export {CreateAppModal}
